// Sidebar compartido por las pantallas del kine (DashboardKine,
// DetallePaciente, SesionesKine, futuras). Antes estaba duplicado
// inline en 3 archivos con el mismo markup.
//
// Active state via prop `activeKey` en lugar de derivar de useLocation.
// Razón: una sección del kine abarca múltiples URLs (ej. "Pacientes"
// cubre /, /paciente/:id, /prescribir/:id) — el match 1:1 que usamos
// en PacienteBottomNav no aplica acá. El componente no sabe sobre
// routing; cada pantalla declara su sección.
//
// Modos:
// - Desktop (≥ md): siempre visible como sidebar fijo.
// - Mobile (< md): oculto por default; se abre como drawer con backdrop
//   cuando `mobileOpen=true`. Click en item, click en backdrop, click en
//   X o ESC cierran el drawer (llamando a `onMobileClose`).

import { useEffect } from "react";
import { Link } from "react-router";
import {
  Activity,
  Calendar,
  Dumbbell,
  LayoutDashboard,
  MessageCircle,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useUnreadCount } from "@/lib/supabase/queries/useUnreadCount";
import { useAuth } from "@/app/components/auth/AuthProvider";

export type KineSidebarKey =
  | "pacientes"
  | "sesiones"
  | "rutinas"
  | "biblioteca"
  | "calendario"
  | "mensajes"
  | "configuracion";

interface SidebarItem {
  key: KineSidebarKey;
  path: string;
  Icon: typeof Users;
  label: string;
}

const items: SidebarItem[] = [
  { key: "pacientes", path: "/", Icon: Users, label: "Pacientes" },
  { key: "sesiones", path: "/sesiones", Icon: Activity, label: "Sesiones" },
  { key: "rutinas", path: "/rutinas", Icon: LayoutDashboard, label: "Rutinas" },
  {
    key: "biblioteca",
    path: "/biblioteca",
    Icon: Dumbbell,
    label: "Biblioteca ejercicios",
  },
  { key: "calendario", path: "/calendario", Icon: Calendar, label: "Calendario" },
  {
    key: "mensajes",
    path: "/mensajes-kine",
    Icon: MessageCircle,
    label: "Mensajes",
  },
  {
    key: "configuracion",
    path: "/configuracion",
    Icon: Settings,
    label: "Configuración",
  },
];

interface Props {
  /** Key del item a destacar como activo. Si la pantalla está en una
   *  URL que no pertenece a ningún item (ej. /sesiones hoy), omitir
   *  la prop y el sidebar queda sin nada destacado. */
  activeKey?: KineSidebarKey;
  /** Estado del drawer en mobile. En desktop el sidebar siempre está
   *  visible (esta prop se ignora). */
  mobileOpen?: boolean;
  /** Disparado al cerrar el drawer (click backdrop, click X, click item, ESC).
   *  La pantalla maneja el state local. */
  onMobileClose?: () => void;
}

export function KineSidebar({
  activeKey,
  mobileOpen = false,
  onMobileClose,
}: Props) {
  // ESC cierra el drawer en mobile.
  useEffect(() => {
    if (!mobileOpen || !onMobileClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMobileClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mobileOpen, onMobileClose]);

  // Unread badge global para item "Mensajes" — solo si el viewer es kine.
  // useUnreadCount devuelve 0 si userRole no es kine (no muestra badge).
  const { userRole } = useAuth();
  const unreadMensajes = useUnreadCount(userRole === "kine" ? "kine" : null);

  const renderItems = (onItemClick?: () => void) => (
    <div className="space-y-1">
      {items.map((item) => {
        const on = item.key === activeKey;
        const showUnreadBadge = item.key === "mensajes" && unreadMensajes > 0;
        return (
          <Link
            key={item.key}
            to={item.path}
            onClick={onItemClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ease-out ${
              on
                ? "bg-primary text-cyan-400 dark:text-primary-foreground font-semibold shadow-md"
                : "hover:bg-sidebar-accent text-sidebar-foreground"
            }`}
          >
            <item.Icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="flex-1">{item.label}</span>
            {showUnreadBadge && (
              <span
                className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center tabular-nums ${
                  on
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-primary text-primary-foreground"
                }`}
              >
                {unreadMensajes > 99 ? "99+" : unreadMensajes}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar — siempre visible md+ */}
      <div className="hidden md:block w-56 bg-sidebar border-r border-sidebar-border min-h-[calc(100vh-73px)] p-4">
        {renderItems()}
      </div>

      {/* Mobile drawer — controlado por mobileOpen */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex animate-in fade-in duration-150"
          role="dialog"
          aria-modal="true"
          aria-label="Menú de navegación"
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <aside className="relative w-64 bg-sidebar border-r border-sidebar-border h-full p-4 animate-in slide-in-from-left duration-200">
            <button
              onClick={onMobileClose}
              className="absolute top-3 right-3 p-1 hover:bg-sidebar-accent rounded transition-colors"
              aria-label="Cerrar menú"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div className="mt-8">{renderItems(onMobileClose)}</div>
          </aside>
        </div>
      )}
    </>
  );
}
